import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useGql,
  Avatar,
  AvatarImage,
  AvatarFallback,
} from '@ah/design';
import { Header, BottomSheet, DetailSkeleton, PullToRefresh, ResizedImage } from '@ah/design/mobile';
import { ModerationMenu, ReportSheet } from '@ah/moderation/mobile';
import { BLOCK_USER_M } from '@ah/moderation/shared';
import {
  ThumbsUp,
  MessageSquare,
  Send,
  MoreHorizontal,
  Trash2,
  Flag,
  ShieldOff,
  CheckCircle2,
  FileText,
  Sparkles,
} from 'lucide-react';
import {
  CIRCLE_QUERY,
  CIRCLE_POSTS_QUERY,
  CREATE_CIRCLE_POST_MUTATION,
  ADD_COMMENT_MUTATION,
  TOGGLE_REACTION_MUTATION,
  DELETE_POST_MUTATION,
  JOIN_CIRCLE_MUTATION,
  CONFIRM_CIRCLE_MUTATION,
  DECLINE_CIRCLE_MUTATION,
  circleCategoryLabel,
  circleDisplayName,
  type Circle,
  type CirclePost,
  type CirclePostComment,
  type CirclePostReaction,
  type HumanCircle,
} from '../shared/index.ts';
import { relativeTime } from './_parts.tsx';

const ME_QUERY = /* GraphQL */ `query Me { me { id name pictureUrl } }`;

interface MeData { id: string; name: string; pictureUrl: string | null }

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isImageMime(mime: string): boolean {
  return mime.startsWith('image/');
}

// Pick the surface title + back route from the circle's surface type, so
// coalition / league detail pages don't read as "Circles".
function surfaceChrome(type: string | null | undefined): { title: string; backTo: string } {
  switch (type) {
    case 'coalition': return { title: 'Coalitions', backTo: '/coalitions' };
    case 'league':    return { title: 'Leagues',    backTo: '/games' };
    default:          return { title: 'Circles',    backTo: '/circles' };
  }
}

// Category-based gradient for the banner when there's no hero image yet.
function gradientForCategory(category: string): string {
  switch (category) {
    case 'organization': return 'from-indigo-500 via-blue-500 to-cyan-500';
    case 'role':         return 'from-violet-500 via-purple-500 to-fuchsia-500';
    case 'industry':     return 'from-emerald-500 via-teal-500 to-cyan-500';
    case 'interest':     return 'from-amber-500 via-orange-500 to-rose-500';
    case 'cause':        return 'from-rose-500 via-pink-500 to-fuchsia-500';
    case 'goal':         return 'from-sky-500 via-blue-500 to-indigo-500';
    default:             return 'from-slate-500 via-slate-400 to-slate-300';
  }
}

export function CircleDetail() {
  const { id } = useParams<{ id: string }>();
  const gql = useGql();
  const qc = useQueryClient();
  const [membersOpen, setMembersOpen] = useState(false);
  const [joining, setJoining] = useState(false);

  const validId = !!id && UUID_RE.test(id);
  const postsKey = ['circles', id, 'posts'];

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const res = await gql.request<{ me: MeData | null }>(ME_QUERY);
      return res.me;
    },
    staleTime: 5 * 60_000,
  });

  const { data: circle, isLoading } = useQuery({
    queryKey: ['circles', id],
    queryFn: async () => {
      const res = await gql.request<{ circle: Circle }>(CIRCLE_QUERY, { id });
      return res.circle;
    },
    enabled: validId,
  });

  const { data: posts } = useQuery({
    queryKey: postsKey,
    queryFn: async () => {
      const res = await gql.request<{ circlePosts: CirclePost[] }>(CIRCLE_POSTS_QUERY, { circleId: id });
      return res.circlePosts;
    },
    enabled: validId,
  });

  const handleToggleReaction = useCallback(
    (postId: string, reactionKey: string) => {
      if (!me) return;
      const prev = qc.getQueryData<CirclePost[]>(postsKey);
      qc.setQueryData<CirclePost[]>(postsKey, (old) =>
        (old ?? []).map((p) => {
          if (p.id !== postId) return p;
          const existing = p.reactions.find((r) => r.user.id === me.id);
          let reactions: CirclePostReaction[];
          if (!existing) {
            reactions = [...p.reactions, { id: `temp-${Date.now()}`, user: { id: me.id, name: me.name }, reaction: reactionKey }];
          } else if (existing.reaction === reactionKey) {
            reactions = p.reactions.filter((r) => r.user.id !== me.id);
          } else {
            reactions = p.reactions.map((r) =>
              r.user.id === me.id ? { ...r, reaction: reactionKey } : r,
            );
          }
          return { ...p, reactions };
        }),
      );
      gql
        .request<{ toggleCirclePostReaction: { id: string; reactions: CirclePostReaction[] } }>(
          TOGGLE_REACTION_MUTATION,
          { postId, reaction: reactionKey },
        )
        .then((res) => {
          qc.setQueryData<CirclePost[]>(postsKey, (old) =>
            (old ?? []).map((p) =>
              p.id === postId ? { ...p, reactions: res.toggleCirclePostReaction.reactions } : p,
            ),
          );
        })
        .catch(() => {
          qc.setQueryData<CirclePost[]>(postsKey, () => prev ?? []);
        });
    },
    [me, qc, gql, id],
  );

  const handleAddComment = useCallback(
    (postId: string, content: string) => {
      if (!me) return;
      const tempId = `temp-${Date.now()}`;
      const tempComment: CirclePostComment = {
        id: tempId,
        author: { id: me.id, name: me.name, pictureUrl: me.pictureUrl },
        content,
        createdAt: new Date().toISOString(),
      };
      qc.setQueryData<CirclePost[]>(postsKey, (old) =>
        (old ?? []).map((p) =>
          p.id === postId ? { ...p, comments: [...p.comments, tempComment] } : p,
        ),
      );
      gql
        .request<{ addCirclePostComment: CirclePostComment }>(ADD_COMMENT_MUTATION, { postId, content })
        .then((res) => {
          qc.setQueryData<CirclePost[]>(postsKey, (old) =>
            (old ?? []).map((p) =>
              p.id === postId
                ? { ...p, comments: p.comments.map((c) => (c.id === tempId ? res.addCirclePostComment : c)) }
                : p,
            ),
          );
        })
        .catch(() => {
          qc.setQueryData<CirclePost[]>(postsKey, (old) =>
            (old ?? []).map((p) =>
              p.id === postId ? { ...p, comments: p.comments.filter((c) => c.id !== tempId) } : p,
            ),
          );
        });
    },
    [me, qc, gql, id],
  );

  const handleDeletePost = useCallback(
    (postId: string) => {
      const prev = qc.getQueryData<CirclePost[]>(postsKey);
      qc.setQueryData<CirclePost[]>(postsKey, (old) => (old ?? []).filter((p) => p.id !== postId));
      gql.request(DELETE_POST_MUTATION, { id: postId }).catch(() => {
        qc.setQueryData<CirclePost[]>(postsKey, () => prev ?? []);
      });
    },
    [qc, gql, id],
  );

  const handleCreatePost = useCallback(
    async (content: string) => {
      if (!me || !id) return;
      const tempId = `temp-${Date.now()}`;
      const optimistic: CirclePost = {
        id: tempId,
        circleId: id,
        author: { id: me.id, name: me.name, pictureUrl: me.pictureUrl },
        content,
        linkUrl: null,
        linkTitle: null,
        linkDescription: null,
        linkImageUrl: null,
        linkDomain: null,
        attachments: [],
        comments: [],
        reactions: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      qc.setQueryData<CirclePost[]>(postsKey, (old) => [optimistic, ...(old ?? [])]);
      try {
        const res = await gql.request<{ createCirclePost: CirclePost }>(
          CREATE_CIRCLE_POST_MUTATION,
          { circleId: id, content, attachments: null },
        );
        qc.setQueryData<CirclePost[]>(postsKey, (old) =>
          (old ?? []).map((p) => (p.id === tempId ? res.createCirclePost : p)),
        );
      } catch {
        qc.setQueryData<CirclePost[]>(postsKey, (old) =>
          (old ?? []).filter((p) => p.id !== tempId),
        );
      }
    },
    [me, qc, gql, id],
  );

  // Refetch every circles list + this circle (so memberCount/members update
  // immediately after join / accept). Cheaper than staleTime=0 everywhere.
  const refreshAfterMembershipChange = () => {
    qc.invalidateQueries({ queryKey: ['circles', id] });
    qc.invalidateQueries({ queryKey: ['circles', 'my'] });
    qc.invalidateQueries({ queryKey: ['circles', 'suggested'] });
  };

  const handleJoin = async () => {
    if (!id) return;
    setJoining(true);
    try {
      const res = await gql.request<{ joinCircle: HumanCircle }>(JOIN_CIRCLE_MUTATION, { circleId: id });
      qc.setQueryData<Circle | undefined>(['circles', id], (old) =>
        old ? { ...old, myStatus: res.joinCircle.status } : old,
      );
      refreshAfterMembershipChange();
      if (res.joinCircle.status === 'confirmed') {
        qc.invalidateQueries({ queryKey: postsKey });
      }
    } finally {
      setJoining(false);
    }
  };

  const handleConfirm = async () => {
    if (!id) return;
    setJoining(true);
    try {
      await gql.request(CONFIRM_CIRCLE_MUTATION, { circleId: id });
      qc.setQueryData<Circle | undefined>(['circles', id], (old) =>
        old ? { ...old, myStatus: 'confirmed' } : old,
      );
      refreshAfterMembershipChange();
      qc.invalidateQueries({ queryKey: postsKey });
    } finally {
      setJoining(false);
    }
  };

  const handleDeclineCircle = async () => {
    if (!id) return;
    await gql.request(DECLINE_CIRCLE_MUTATION, { circleId: id });
    qc.setQueryData<Circle | undefined>(['circles', id], (old) =>
      old ? { ...old, myStatus: 'declined' } : old,
    );
    refreshAfterMembershipChange();
  };

  if (isLoading || !circle) {
    return (
      <div className="fixed inset-0 flex flex-col bg-background">
        <Header title="" backTo="/circles" />
        <main className="flex-1 overflow-y-auto">
          <DetailSkeleton hero="avatar" />
        </main>
      </div>
    );
  }

  const chrome = surfaceChrome(circle.type);

  const isMember = circle.myStatus === 'confirmed';
  const isDirectInvite = circle.myStatus === 'invited' || circle.myStatus === 'pending';
  const isInferred = circle.myStatus === 'inferred';
  const preview = circle.members.slice(0, 12);
  const overflow = Math.max(0, circle.members.length - preview.length);
  const title = circleDisplayName(circle.displayTitle ?? circle.name);
  const gradient = gradientForCategory(circle.category);

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header
        title={chrome.title}
        backTo={chrome.backTo}
        trailing={
          <ModerationMenu
            targetType="circle"
            targetId={circle.id}
            reportTitle="Report this circle"
          />
        }
      />
      <PullToRefresh
        className="flex-1 pb-28"
        onRefresh={async () => {
          await Promise.all([
            qc.invalidateQueries({ queryKey: ['circles', id] }),
            qc.invalidateQueries({ queryKey: postsKey }),
          ]);
        }}
      >
        {/* ── Banner ─────────────────────────────────────────────── */}
        <div className={`relative h-32 ${circle.coverUrl ? '' : `bg-gradient-to-br ${gradient}`}`}>
          {circle.coverUrl ? (
            <ResizedImage src={circle.coverUrl} renderWidth={384} fallback={false} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : null}
        </div>

        {/* ── Title + meta ───────────────────────────────────────── */}
        <section className="pt-3 px-4 pb-3">
          <h1 className="text-xl font-semibold leading-tight">{title}</h1>
          <p className="text-xs text-muted-foreground mt-1">
            {circle.memberCount} member{circle.memberCount !== 1 ? 's' : ''}
          </p>
        </section>

        {/* ── Join / invite actions ──────────────────────────────── */}
        {!isMember ? (
          <div className="px-4 pb-3">
            {isDirectInvite ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={joining}
                  onClick={handleConfirm}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium py-2 disabled:opacity-60"
                >
                  <CheckCircle2 size={14} /> {joining ? 'Joining…' : 'Accept invite'}
                </button>
                <button
                  type="button"
                  disabled={joining}
                  onClick={handleDeclineCircle}
                  className="inline-flex items-center justify-center rounded-full border border-border text-sm text-muted-foreground px-4 py-2"
                >
                  Decline
                </button>
              </div>
            ) : isInferred ? (
              <button
                type="button"
                disabled={joining}
                onClick={handleJoin}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium py-2 disabled:opacity-60"
              >
                <Sparkles size={14} /> {joining ? 'Joining…' : 'Accept invite'}
              </button>
            ) : (
              <button
                type="button"
                disabled={joining}
                onClick={handleJoin}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground text-sm font-medium py-2 disabled:opacity-60"
              >
                <CheckCircle2 size={14} /> {joining ? 'Joining…' : 'Join Circle'}
              </button>
            )}
          </div>
        ) : null}

        {/* ── Description card ───────────────────────────────────── */}
        {circle.description ? (
          <section className="px-4 pb-3">
            <div className="rounded-xl border border-border bg-card p-3">
              <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                About
              </h2>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{circle.description}</p>
            </div>
          </section>
        ) : null}

        {/* ── Members strip ──────────────────────────────────────── */}
        {circle.members.length > 0 ? (
          <section className="px-4 pb-4">
            <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Members
            </h2>
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
              {preview.map((m) => (
                <Avatar key={m.id} className="h-10 w-10 shrink-0">
                  {m.human.pictureUrl ? <AvatarImage src={m.human.pictureUrl} alt={m.human.name} /> : null}
                  <AvatarFallback className="text-xs">{m.human.name[0]}</AvatarFallback>
                </Avatar>
              ))}
              <button
                type="button"
                onClick={() => setMembersOpen(true)}
                className="shrink-0 h-10 px-3 rounded-full border border-border text-xs text-muted-foreground"
              >
                {overflow > 0 ? `+${overflow} more` : 'See all'}
              </button>
            </div>
          </section>
        ) : null}

        {/* ── Posts feed ─────────────────────────────────────────── */}
        <section className="px-3 space-y-3">
          {posts && posts.length > 0 ? (
            posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                currentUserId={me?.id ?? null}
                onToggleReaction={handleToggleReaction}
                onAddComment={handleAddComment}
                onDeletePost={handleDeletePost}
              />
            ))
          ) : (
            <p className="text-center py-10 text-sm text-muted-foreground">
              No posts yet — be the first to share something.
            </p>
          )}
        </section>
      </PullToRefresh>

      {isMember && me ? <Composer onSubmit={handleCreatePost} /> : null}

      <BottomSheet
        open={membersOpen}
        onClose={() => setMembersOpen(false)}
        title={`Members (${circle.members.length})`}
      >
        <ul className="divide-y divide-border">
          {circle.members.map((m) => (
            <li key={m.id} className="flex items-center gap-3 px-4 py-3">
              <Avatar className="h-9 w-9">
                {m.human.pictureUrl ? <AvatarImage src={m.human.pictureUrl} alt={m.human.name} /> : null}
                <AvatarFallback>{m.human.name[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.human.name}</p>
                {m.status !== 'confirmed' ? (
                  <p className="text-[10px] text-muted-foreground capitalize">
                    {m.status === 'inferred' ? 'Invited' : m.status}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </BottomSheet>
    </div>
  );
}

function Composer({ onSubmit }: { onSubmit: (content: string) => Promise<void> | void }) {
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const content = value.trim();
    if (!content || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(content);
      setValue('');
      setFocused(false);
    } finally {
      setSubmitting(false);
    }
  };

  // The focused composer is a full-screen overlay. Two iOS gotchas:
  //   1. The status bar (time / battery / dynamic island) sits at the top
  //      of the WebView. Without `padding-top: env(safe-area-inset-top)`,
  //      the Cancel/Post bar slides up behind it and becomes unclickable.
  //   2. When the soft keyboard opens, the layout viewport doesn't shrink
  //      automatically — `h-screen` / `inset-0` still measure the full
  //      screen, so the textarea sticks out under the keyboard and the
  //      caret ends up off-screen. `h-dvh` (dynamic viewport height) DOES
  //      track the keyboard on iOS 17+ WebKit, which is what Capacitor
  //      uses, so the textarea naturally shrinks to the visible region.
  return (
    <div
      className={
        focused
          ? 'fixed inset-x-0 top-0 z-40 bg-background flex flex-col h-dvh'
          : 'fixed left-0 right-0 bottom-0 z-30 bg-background border-t border-border'
      }
      style={{
        paddingTop: focused ? 'env(safe-area-inset-top)' : 0,
        paddingBottom: focused ? 0 : 'env(safe-area-inset-bottom)',
      }}
    >
      {focused ? (
        <div className="shrink-0 flex items-center justify-between h-12 px-3 border-b border-border">
          <button
            type="button"
            onClick={() => setFocused(false)}
            className="text-sm text-muted-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!value.trim() || submitting}
            onClick={handleSubmit}
            className="text-sm font-medium text-primary disabled:opacity-50"
          >
            {submitting ? 'Posting…' : 'Post'}
          </button>
        </div>
      ) : null}
      <div className={focused ? 'flex-1 min-h-0 p-3' : 'p-3 flex items-center gap-2'}>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setFocused(true)}
          autoFocus={focused}
          placeholder="Share something with this circle…"
          className={
            focused
              ? 'w-full h-full resize-none bg-transparent outline-none text-sm'
              : 'flex-1 h-9 resize-none bg-muted/50 rounded-full px-3 py-2 text-sm outline-none'
          }
          rows={focused ? undefined : 1}
        />
        {!focused ? (
          <button
            type="button"
            disabled={!value.trim() || submitting}
            onClick={handleSubmit}
            className="shrink-0 w-9 h-9 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center disabled:opacity-50"
            aria-label="Post"
          >
            <Send size={16} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function PostCard({
  post,
  currentUserId,
  onToggleReaction,
  onAddComment,
  onDeletePost,
}: {
  post: CirclePost;
  currentUserId: string | null;
  onToggleReaction: (postId: string, reaction: string) => void;
  onAddComment: (postId: string, content: string) => void;
  onDeletePost: (postId: string) => void;
}) {
  const gql = useGql();
  const [commentText, setCommentText] = useState('');
  const [showComments, setShowComments] = useState(post.comments.length > 0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const isAuthor = currentUserId === post.author.id;
  const isPending = post.id.startsWith('temp-');
  // Apple App Review (Guideline 1.2) requires Report + Block to be
  // reachable on every UGC surface — circle posts included, not just
  // the circle header. Non-authors see Report + Block; authors see the
  // existing Delete action.
  const blockMutation = useMutation({
    mutationFn: () => gql.request(BLOCK_USER_M, { humanId: post.author.id }),
    onSuccess: () => setMenuOpen(false),
  });
  const myReaction = currentUserId
    ? post.reactions.find((r) => r.user.id === currentUserId)
    : null;
  const likeCount = post.reactions.length;

  const submitComment = () => {
    const content = commentText.trim();
    if (!content) return;
    onAddComment(post.id, content);
    setCommentText('');
  };

  return (
    <article
      className={
        'rounded-xl border border-border bg-card p-3 space-y-3' + (isPending ? ' opacity-70' : '')
      }
    >
      <div className="flex items-center gap-2">
        <Avatar className="h-8 w-8">
          {post.author.pictureUrl ? <AvatarImage src={post.author.pictureUrl} /> : null}
          <AvatarFallback>{post.author.name[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{post.author.name}</p>
          <p className="text-[11px] text-muted-foreground">{relativeTime(post.createdAt)}</p>
        </div>
        {!isPending ? (
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="p-1 rounded hover:bg-accent text-muted-foreground"
            aria-label="Post actions"
          >
            <MoreHorizontal size={16} />
          </button>
        ) : null}
      </div>

      <p className="text-sm whitespace-pre-wrap break-words">{post.content}</p>

      {post.linkUrl && post.linkTitle ? (
        <a
          href={post.linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-lg border border-border overflow-hidden"
        >
          {post.linkImageUrl ? (
            <ResizedImage src={post.linkImageUrl} renderWidth={384} fallback={false} alt="" className="w-full max-h-40 object-cover" />
          ) : null}
          <div className="p-2.5">
            <p className="text-sm font-medium line-clamp-2">{post.linkTitle}</p>
            {post.linkDescription ? (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                {post.linkDescription}
              </p>
            ) : null}
            {post.linkDomain ? (
              <p className="text-[10px] text-muted-foreground mt-1">{post.linkDomain}</p>
            ) : null}
          </div>
        </a>
      ) : null}

      {post.attachments.length > 0 ? (
        <div className="space-y-2">
          {post.attachments.filter((a) => isImageMime(a.mimeType)).map((a) => (
            <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer">
              <ResizedImage
                src={a.url}
                renderWidth={384}
                fallback={false}
                alt={a.originalName}
                className="w-full max-h-80 object-cover rounded-lg border border-border"
              />
            </a>
          ))}
          {post.attachments.filter((a) => !isImageMime(a.mimeType)).map((a) => (
            <a
              key={a.id}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg border border-border px-3 py-2"
            >
              <FileText size={14} className="text-muted-foreground shrink-0" />
              <span className="text-xs truncate">{a.originalName}</span>
            </a>
          ))}
        </div>
      ) : null}

      <div className="flex items-center gap-1 pt-1 border-t border-border">
        <button
          type="button"
          onClick={() => onToggleReaction(post.id, 'like')}
          className={
            'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs ' +
            (myReaction ? 'text-primary font-medium' : 'text-muted-foreground')
          }
        >
          <ThumbsUp size={14} />
          {likeCount > 0 ? likeCount : 'Like'}
        </button>
        <button
          type="button"
          onClick={() => setShowComments((s) => !s)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-muted-foreground"
        >
          <MessageSquare size={14} />
          {post.comments.length > 0 ? post.comments.length : 'Comment'}
        </button>
      </div>

      {showComments ? (
        <div className="space-y-2 pt-2 border-t border-border">
          {post.comments.map((c) => (
            <div key={c.id} className="flex gap-2">
              <Avatar className="h-6 w-6 shrink-0">
                {c.author.pictureUrl ? <AvatarImage src={c.author.pictureUrl} /> : null}
                <AvatarFallback className="text-[10px]">{c.author.name[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="bg-muted rounded-lg px-2.5 py-1.5 text-xs inline-block max-w-full">
                  <span className="font-medium">{c.author.name}</span>
                  <p className="break-words">{c.content}</p>
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5 px-1">
                  {relativeTime(c.createdAt)}
                </p>
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Write a comment…"
              className="flex-1 rounded-full border border-border px-3 py-1.5 text-xs bg-transparent outline-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && commentText.trim()) {
                  e.preventDefault();
                  submitComment();
                }
              }}
            />
            <button
              type="button"
              onClick={submitComment}
              disabled={!commentText.trim()}
              className="shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center disabled:opacity-50"
              aria-label="Send comment"
            >
              <Send size={13} />
            </button>
          </div>
        </div>
      ) : null}

      <BottomSheet open={menuOpen} onClose={() => setMenuOpen(false)} title="Post">
        {isAuthor ? (
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onDeletePost(post.id);
            }}
            className="flex items-center gap-2 w-full px-4 py-3 text-sm text-destructive"
          >
            <Trash2 size={14} /> Delete post
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setReportOpen(true);
              }}
              className="flex items-center gap-2 w-full px-4 py-3 text-sm"
            >
              <Flag size={14} /> Report post
            </button>
            <button
              type="button"
              onClick={() => blockMutation.mutate()}
              disabled={blockMutation.isPending}
              className="flex items-center gap-2 w-full px-4 py-3 text-sm text-destructive disabled:opacity-50"
            >
              <ShieldOff size={14} />
              {blockMutation.isPending
                ? 'Blocking…'
                : `Block ${post.author.name.split(' ')[0]}`}
            </button>
          </>
        )}
      </BottomSheet>

      <ReportSheet
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        targetType="circle_post"
        targetId={post.id}
        title="Report this post"
      />
    </article>
  );
}
